import React, { FC, useMemo, useState } from "react";
import NextLink from "next/link";
import Image from "next/legacy/image";
import { Alert, AlertIcon, Box, Button, Center, Container, Flex, Link, Text, VStack } from "@chakra-ui/react";
import type { InferGetServerSidePropsType, NextPage } from "next";
import { TextInput } from "../components/TextInput";
import { useForm } from "../hooks/useForm";
import { useRouter } from "next/router";
import { getAuth, sendPasswordResetEmail } from "firebase/auth";
import MetaTags from "../components/MetaTags";
import { IGetServerSidePropsContext, withAppContext } from "../utils/ssr_util";
import { AuthAction, withAuthUser } from "next-firebase-auth";
interface ResetPasswordFormData {
  email: string;
}
interface IResetPasswordPageProps extends InferGetServerSidePropsType<typeof fetchDataRequirements> {}
const ResetPasswordPage: FC<IResetPasswordPageProps> = ({
  emailPrefill
}) => {
  const router = useRouter();
  const {
    data,
    errors,
    handleChange,
    handleSubmit,
    resetFormData
  } = useForm<ResetPasswordFormData>({
    initialValues: {
      email: emailPrefill as string ?? ""
    },
    validation: {
      email: {
        required: {
          value: true,
          message: "Please enter your email address."
        },
        email: {
          value: true,
          message: "Please enter a valid email address."
        }
      }
    },
    onSubmit: async (data: any) => {
      setErrorMessage("");
      setLoading(true);
      try {
        await sendPasswordResetEmail(getAuth(), data.email);
        router.push(`/login?password_reset_sent=true&email=${encodeURIComponent(data.email)}`);
        setLoading(false);
      } catch (e: any) {
        setLoading(false);
        setErrorMessage(e.message);
      }
    }
  });
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isLoading, setLoading] = useState<boolean>(false);
  return <Container data-sentry-element="Container" data-sentry-component="ResetPasswordPage" data-sentry-source-file="reset-password.tsx">
            <MetaTags title="Reset password" description="Reset your LiveLink AI password securely and regain access to your account. Follow the simple steps to create a new password in just a few minutes." data-sentry-element="MetaTags" data-sentry-source-file="reset-password.tsx" />
            <Flex direction="column" justifyContent="center" pt="200px" data-sentry-element="Flex" data-sentry-source-file="reset-password.tsx">
                <VStack spacing="6" data-sentry-element="VStack" data-sentry-source-file="reset-password.tsx">
                    <NextLink href="/" passHref legacyBehavior data-sentry-element="NextLink" data-sentry-source-file="reset-password.tsx">
                        <Box as="a" data-sentry-element="Box" data-sentry-source-file="reset-password.tsx">
                            <Image src="/images/livelink_logo.svg" width={75} height={75} alt="LiveLink Logo" data-sentry-element="Image" data-sentry-source-file="reset-password.tsx" />
                        </Box>
                    </NextLink>

                    <Text fontSize="30px" fontWeight="bold" data-sentry-element="Text" data-sentry-source-file="reset-password.tsx">
                        Reset Password
                    </Text>
                    <form onSubmit={handleSubmit} style={{
          width: "100%"
        }}>
                        <VStack spacing={5} data-sentry-element="VStack" data-sentry-source-file="reset-password.tsx">
                            <TextInput inputProps={{
              id: "email",
              name: "email",
              placeholder: "Email",
              value: data.email,
              onChange: handleChange("email")
            }} errorMessage={errors.email} data-sentry-element="TextInput" data-sentry-source-file="reset-password.tsx" />
                            {errorMessage && <Alert status="error">
                                    <AlertIcon />
                                    {errorMessage}
                                </Alert>}
                            <Button type="submit" variant="primary" width="100%" size="lg" isLoading={isLoading} data-sentry-element="Button" data-sentry-source-file="reset-password.tsx">
                                <Text as="span" color="currentcolor" data-sentry-element="Text" data-sentry-source-file="reset-password.tsx">Send password reset email</Text>
                            </Button>
                        </VStack>
                    </form>
                </VStack>
                <Center mt={5} data-sentry-element="Center" data-sentry-source-file="reset-password.tsx">
                    <Link textDecoration="underline" fontWeight="semibold" onClick={router.back} data-sentry-element="Link" data-sentry-source-file="reset-password.tsx">
                        Cancel
                    </Link>
                </Center>
            </Flex>
        </Container>;
};
const fetchDataRequirements = async ({
  query
}: IGetServerSidePropsContext) => {
  const {
    email
  } = query;
  return {
    props: {
      emailPrefill: email ? decodeURIComponent(email as string) : null
    }
  };
};
export const getServerSideProps = withAppContext(fetchDataRequirements);
export default withAuthUser({
  whenAuthed: AuthAction.REDIRECT_TO_APP
})(ResetPasswordPage);